<template>
    <SCSidebar v-model:visible="visible" title="Cluster Update Status">
        <div v-if="!loaded">Loading...</div>
        <SCForm v-if="loaded" saveLabel="Retry Update" cancelLabel="Close" :showCancel="true" :showSave="showRetryOptions" :saveDisabled="!selectedVersion" @save="handleRetrySave" @cancel="handleDialogClose">
            <SCFormStep>
            <SCPropertyList stacked>
                <SCProperty label="Cluster Name" :value="cluster.data.name" />
                <SCProperty label="Current Version" :value="cluster.data.version" />
                <SCProperty label="Updating To" :value="updateToVersion" v-if="updateToVersion" />
                <SCProperty label="Update Status" :value="updateState">
                    <Progress :value="updatePercent" :color="progressBarColor" />
                    <div class="hint p-mt-1 p-d-flex p-ai-center">
                        <SCIcon v-if="hasUpdateError" name="error" iconColor="error" :width="16" class="p-mr-1" />
                        <span class="update-status-msg">{{ updateState }} {{ updateMessage }}</span>
                    </div>
                </SCProperty>
            </SCPropertyList>
            <!-- title-only section: content renders as a sibling so it isn't
                 indented by the section's content padding -->
            <SCSection v-if="sectionTitle" :title="sectionTitle" :showSeparator="false" />
            <SCParagraph v-if="showSupportGuidance">{{ supportMessage }} <SCLink title="Contact Scale Computing support" :route="supportUrl" target="_blank" /></SCParagraph>
            <SCRadioGroup v-if="showRetryOptions" title="Available Update(s)" :showSeparator="true" :modelValue="selectedVersion">
                <template #group>
                    <SCRadio v-for="option in updateOptions" :key="option.uuid" name="selectedVersion" v-model="selectedVersion" :value="option.uuid">
                        <div v-html="option.description"></div>
                        <template #description>
                            <SCParagraph v-if="option.uuid === updateToVersion">Retry the update that failed</SCParagraph>
                            <SCAccordion title="Release Notes" v-if="option.changeLog.length">
                                <SCParagraph v-html="option.changeLog"></SCParagraph>
                            </SCAccordion>
                            <SCParagraph v-else class="scale-no-release-notes">No release notes available</SCParagraph>
                        </template>
                    </SCRadio>
                </template>
            </SCRadioGroup>
            </SCFormStep>
        </SCForm>
    </SCSidebar>
</template>
<script>
import config from '../../config';
import { isUpdateErrorState, isUpdateRetryableState, markUpdateSent } from '@/lib/cluster-updates';
import { cluster } from '@/lib/nucleus';
import { checkFlag } from '@/lib/statsig';
import EventBus from '@/layouts/slots/event-bus';
import Progress from '../shared/Progress.vue';
import { SCAccordion, SCForm, SCFormStep, SCIcon, SCLink, SCParagraph, SCProperty, SCPropertyList, SCRadio, SCRadioGroup, SCSection, SCSidebar } from '@scale-computing/scale-design-framework';
import { compareVersions, validate } from 'compare-versions';
import { rg4js } from '../../lib/raygun';
export default {
    components: { Progress, SCAccordion, SCForm, SCFormStep, SCIcon, SCLink, SCParagraph, SCProperty, SCPropertyList, SCRadio, SCRadioGroup, SCSection, SCSidebar },
    data() {
        return {
            manualUUID: null,
            visible: false,
            loading: false,
            loaded: false,
            clusterId: null,
            cluster: null,
            interval: null,
            selectedVersion: null,
        };
    },
    watch: {
        visible(v) {
            if (!v) clearInterval(this.interval);
        },
    },
    computed: {
        // the footer renders before the first load resolves; tolerate a null cluster
        updateStatus() {
            return this.cluster?.data?.updatesStatus ?? {};
        },
        updatePercent() {
            return this.updateStatus.percent ?? 0;
        },
        updateState() {
            return this.updateStatus.state ?? null;
        },
        updateMessage() {
            const message = this.updateStatus.message ?? '';
            return message.length > 0 ? `- ${message}` : '';
        },
        updateToVersion() {
            return this.updateStatus.toVersion || null;
        },
        updateFailNotes() {
            return this.updateStatus.failNotes || '';
        },
        hasUpdateError() {
            return isUpdateErrorState(this.updateState);
        },
        updateOptions() {
            return this.cluster?.data?.updatesAvailableOptions ?? [];
        },
        // a paused update holds its state file, so scupdate rejects a new
        // apply until it is canceled on the cluster
        isUpdatePaused() {
            return this.hasUpdateError && !isUpdateRetryableState(this.updateState);
        },
        // the cluster reports the versions it can be updated to, so with none
        // there is nothing to resend. Getting here is odd enough to be worth a
        // support call: a broken path to the update server, or a version whose
        // upgrade path we pulled (#4822)
        hasNoUpdateToRetry() {
            return isUpdateRetryableState(this.updateState) && this.updateOptions.length === 0;
        },
        // the update stopped and the user can't act on it from here
        showSupportGuidance() {
            return this.isUpdatePaused || this.hasNoUpdateToRetry;
        },
        supportMessage() {
            if (this.isUpdatePaused) return 'This update is paused and needs assistance to continue.';
            return 'No updates are currently available to retry. If you believe this is in error';
        },
        supportUrl() {
            return config.SCALE_SUPPORT_URL;
        },
        // fail notes are the heading in every state that shows them; the node
        // may break them across lines, collapse it so a heading stays a line
        failNotesLine() {
            return this.updateFailNotes.replace(/\s+/g, ' ').trim();
        },
        sectionTitle() {
            if (this.failNotesLine) return this.failNotesLine;
            if (this.isUpdatePaused) return 'Update paused';
            if (this.hasNoUpdateToRetry) return 'No updates available';
            return this.showRetryOptions ? 'Choose a version to retry' : '';
        },
        // a failed update is retried from here, so the versions are inline
        showRetryOptions() {
            return isUpdateRetryableState(this.updateState) && this.updateOptions.length > 0;
        },
        progressBarColor() {
            let color = 'success';
            let state = this.updateState;
            if (this.hasUpdateError) {
                color = 'error';
            } else if (state === 'PENDING') {
                color = '';
            }
            return color;
        },
    },
    mounted() {
        EventBus.on('show-cluster-update-status-modal', (payload) => {
            this.show(payload.clusterId);
        });
    },
    methods: {
        handleDialogClose() {
            this.visible = false;
            clearInterval(this.interval);
        },
        // acknowledge the failure by resending an update of the user's choice
        // (#1082). SCForm's save flow drives the saving state via done().
        async handleRetrySave(data, done) {
            const uuid = this.selectedVersion;
            if (!uuid) return done();
            try {
                const commandSend = checkFlag('clusters-fleet') ? cluster.commandSend3 : cluster.commandSend;
                const response = await commandSend({
                    id: this.clusterId,
                    command: 'IcosUpdateApply',
                    payload: { uuid },
                });
                // both clients report 401/403/404 through the global error
                // banner and resolve with no data; don't toast success on top
                if (!response?.data) return done();

                // show the cluster as updating until it reports the new cycle,
                // so the failure the user just acted on clears right away
                markUpdateSent(this.clusterId, this.updateStatus);

                done();
                this.handleDialogClose();
                this.$toast.add({ group: 'messages', severity: 'success', summary: 'Update request sent. The update will start in a few moments.', life: 5000 });
            } catch (e) {
                done(e.message);
                rg4js('send', { error: e });
                this.$toast.add({ group: 'messages', severity: 'error', summary: 'Error sending update request', detail: e.message });
            }
        },
        show(clusterId) {
            // reopening while already visible would orphan the running poller
            clearInterval(this.interval);
            // reset before loading so a reopen doesn't flash the previous cluster
            this.clusterId = clusterId;
            this.cluster = null;
            this.loaded = false;
            this.selectedVersion = null;
            this.visible = true;
            this.load();

            this.interval = setInterval(() => {
                this.load();
            }, 10000);
        },
        async load() {
            try {
                this.loading = true;
                // clusters-fleet: fleet's v3 cluster has the same update fields
                const getById = checkFlag('clusters-fleet') ? cluster.getById3 : cluster.getById2;
                const response = await getById({ id: this.clusterId });
                this.cluster = response;
                this.sortUpdateOptions();
                // default to the version that failed, newest otherwise. only on
                // first load, so polling does not fight the user's selection
                if (!this.selectedVersion) {
                    const options = this.updateOptions;
                    const failed = options.find((o) => o.uuid === this.updateToVersion);
                    this.selectedVersion = failed?.uuid ?? options[0]?.uuid ?? null;
                }
                this.loaded = true;
            } catch (e) {
                rg4js('send', { error: e});
                this.$toast.add({ group: "messages",  severity: 'error', summary: `Unable to load cluster`, detail: e.message });
            } finally {
                this.loading = false;
            }
        },
        sortUpdateOptions() {
            const options = this.cluster?.data?.updatesAvailableOptions;
            if (!options?.length) return;
            // compareVersions throws on anything non-semver (simulated clusters
            // report versions like SIM-X.X.X), so only sort when it can't
            if (!options.every((o) => validate(o.uuid))) return;
            options.sort((a, b) => -compareVersions(a.uuid, b.uuid));
        },
    },
};
</script>
